"use client";

import useTranslate from "@app/_hooks/useTranslate";
import { cn } from "@/lib/utils";
import { SearchIcon } from "lucide-react";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import { useCarousel } from "./carousel";
import { Dialog } from "./dialog";

const LightboxDialog = dynamic(
  () => import("./lightbox-dialog").then((mod) => mod.LightboxDialog),
  { ssr: false },
);

type LightboxProps = {
  className?: string;
};

function Lightbox({ className }: LightboxProps) {
  const t = useTranslate();
  const { api, selectedIndex } = useCarousel();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const apiRef = useRef(api);
  const openAtRef = useRef<(index: number) => void>(() => undefined);

  function openAt(index: number) {
    apiRef.current?.scrollTo(index, true);
    setMounted(true);
    setOpen(true);
  }

  useEffect(() => {
    apiRef.current = api;
    openAtRef.current = openAt;
  });

  useEffect(() => {
    const root = api?.rootNode();
    if (!root || !api) return;

    root.classList.add("cursor-zoom-in");

    let pressed = false;
    let moved = false;
    let startX = 0;
    let startY = 0;

    const onPointerDown = (event: PointerEvent) => {
      pressed = true;
      moved = false;
      startX = event.clientX;
      startY = event.clientY;
    };
    const onPointerMove = (event: PointerEvent) => {
      if (!pressed) return;
      if (Math.hypot(event.clientX - startX, event.clientY - startY) > 8) {
        moved = true;
      }
    };
    const onPointerUp = () => {
      pressed = false;
    };
    const onClick = (event: MouseEvent) => {
      if (moved) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("button, a, input, textarea, select, label")) return;

      const slide = target.closest("[data-slot='carousel-item']");
      if (!(slide instanceof HTMLElement) || !root.contains(slide)) return;

      const index = api.slideNodes().indexOf(slide);
      if (index < 0) return;
      openAtRef.current(index);
    };

    root.addEventListener("pointerdown", onPointerDown);
    root.addEventListener("pointermove", onPointerMove);
    root.addEventListener("pointerup", onPointerUp);
    root.addEventListener("click", onClick);

    return () => {
      root.classList.remove("cursor-zoom-in");
      root.removeEventListener("pointerdown", onPointerDown);
      root.removeEventListener("pointermove", onPointerMove);
      root.removeEventListener("pointerup", onPointerUp);
      root.removeEventListener("click", onClick);
    };
  }, [api]);

  useEffect(() => {
    if (!open || !api) return;

    const videos = api
      .slideNodes()
      .flatMap((node) => Array.from(node.querySelectorAll("video")));

    for (const video of videos) video.pause();

    return () => {
      for (const video of videos) {
        if (video.autoplay) void video.play().catch(() => undefined);
      }
    };
  }, [api, open]);

  return (
    <>
      <button
        type="button"
        data-slot="lightbox-trigger"
        className={cn(
          "absolute top-3 right-4 z-10 cursor-pointer p-2 mix-blend-difference",
          className,
        )}
        aria-label={t("openGallery", { strict: true }) ?? "Open gallery"}
        onClick={() => openAt(selectedIndex)}
      >
        <SearchIcon strokeWidth={1} className="size-6 text-white" />
      </button>
      {mounted ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <LightboxDialog open={open} onClose={() => setOpen(false)} />
        </Dialog>
      ) : null}
    </>
  );
}

export { Lightbox };
